import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import { format } from "date-fns";

import PageHeader from "components/PageHeader";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";

import useEventData from "layouts/tables/data/eventTableData";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function eventScopeMeta(event) {
  if (event.department_id) {
    return { label: event.department?.name || "Department", bgcolor: "#0288D1", color: "#fff" };
  }
  if (event.ministry_id) {
    return { label: event.ministry?.name || "Ministry", bgcolor: "#ED6C02", color: "#fff" };
  }
  return { label: "General", bgcolor: "#15A37F", color: "#fff" };
}

function EventChip({ event }) {
  const scope = eventScopeMeta(event);
  return (
    <Chip
      size="small"
      label={event.is_done ? `✓ ${event.title}` : event.title}
      title={scope.label}
      sx={{
        bgcolor: event.is_done ? "action.disabledBackground" : scope.bgcolor,
        color: event.is_done ? "text.secondary" : scope.color,
        fontWeight: 700,
        maxWidth: "100%",
        justifyContent: "flex-start",
        mb: 0.5,
        textDecoration: event.is_done ? "line-through" : "none",
      }}
    />
  );
}
EventChip.propTypes = {
  event: PropTypes.shape({
    title: PropTypes.string,
    department_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ministry_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    department: PropTypes.shape({ name: PropTypes.string }),
    ministry: PropTypes.shape({ name: PropTypes.string }),
    is_done: PropTypes.bool,
  }).isRequired,
};

function Events() {
  const {
    currentMonth,
    calendarDays,
    isLoading,
    error,
    goToPrevMonth,
    goToNextMonth,
    goToToday,
    eventsForDay,
    isSameMonth,
    isToday,
    events,
    departments,
    ministries,
    isGroupHead,
    createModalOpen,
    setCreateModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedEvent,
    form,
    setForm,
    toggleChannel,
    handleAddEvent,
    handleCreateSubmit,
    handleDelete,
    handleDeleteConfirm,
    isSaving,
  } = useEventData();

  const upcoming = events
    .filter((e) => new Date(e.start_at) >= new Date(new Date().setHours(0, 0, 0, 0)))
    .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

  return (
    <Box>
      <PageHeader
        title="Calendar"
        subtitle="Events and meetings — create one and optionally notify members by SMS or email."
        actions={
          <>
            <IconButton onClick={goToPrevMonth} sx={{ border: 1, borderColor: "divider" }}>
              <Icon>chevron_left</Icon>
            </IconButton>
            <Button variant="outlined" onClick={goToToday}>
              Today
            </Button>
            <IconButton onClick={goToNextMonth} sx={{ border: 1, borderColor: "divider" }}>
              <Icon>chevron_right</Icon>
            </IconButton>
            <Button variant="contained" onClick={() => handleAddEvent()}>
              <Icon>add</Icon>&nbsp;New Event
            </Button>
          </>
        }
      />

      <Typography variant="h5" sx={{ mb: 2 }}>
        {format(currentMonth, "MMMM yyyy")}
      </Typography>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          Error: {error}
        </Typography>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ p: 2 }}>
            <Grid container>
              {WEEKDAYS.map((d) => (
                <Grid item xs={12 / 7} key={d}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ display: "block", textAlign: "center", py: 1 }}
                  >
                    {d}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            <Grid container>
              {calendarDays.map((day) => {
                const dayEvents = eventsForDay(day);
                return (
                  <Grid item xs={12 / 7} key={day.toISOString()}>
                    <Box
                      onClick={() => handleAddEvent(day)}
                      sx={{
                        minHeight: 92,
                        border: "1px solid",
                        borderColor: "divider",
                        p: 0.75,
                        opacity: isSameMonth(day) ? 1 : 0.4,
                        bgcolor: isToday(day) ? "info.lighter" : "transparent",
                        cursor: "pointer",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={isToday(day) ? 800 : 500}
                        color={isToday(day) ? "info.dark" : "text.secondary"}
                      >
                        {format(day, "d")}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {dayEvents.slice(0, 2).map((e) => (
                          <EventChip key={e.id} event={e} />
                        ))}
                        {dayEvents.length > 2 && (
                          <Typography variant="caption" color="text.secondary">
                            +{dayEvents.length - 2} more
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Upcoming events
            </Typography>
            {isLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            ) : upcoming.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No upcoming events this month.
              </Typography>
            ) : (
              upcoming.map((e) => (
                <Box
                  key={e.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    py: 1.25,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box>
                    <Typography variant="button" fontWeight={700} display="block">
                      {e.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {format(new Date(e.start_at), "EEE, MMM d · h:mma")}
                      {e.location ? ` · ${e.location}` : ""}
                    </Typography>
                    <Chip
                      size="small"
                      label={eventScopeMeta(e).label}
                      sx={{
                        mt: 0.5,
                        bgcolor: eventScopeMeta(e).bgcolor,
                        color: eventScopeMeta(e).color,
                        height: 20,
                        fontSize: 11,
                      }}
                    />
                  </Box>
                  <IconButton size="small" onClick={() => handleDelete(e)}>
                    <Icon fontSize="small">delete_outline</Icon>
                  </IconButton>
                </Box>
              ))
            )}
          </Card>
        </Grid>
      </Grid>

      <FormDialog
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="New Event"
        onSave={handleCreateSubmit}
        saveLabel={isSaving ? "Saving..." : "Create Event"}
        saveDisabled={!form.title.trim() || !form.start_at || isSaving}
        loading={isSaving}
        maxWidth="sm"
      >
        <FormSection>
          <TextField
            label="Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
          <TextField
            label="Location"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            fullWidth
            margin="normal"
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Starts"
              type="datetime-local"
              value={form.start_at}
              onChange={(e) => setForm({ ...form, start_at: e.target.value })}
              fullWidth
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Ends (optional)"
              type="datetime-local"
              value={form.end_at}
              onChange={(e) => setForm({ ...form, end_at: e.target.value })}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </FormSection>

        <FormSection>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Event scope
          </Typography>
          {isGroupHead ? (
            <Chip
              size="small"
              color="info"
              label="This event will be scoped to your department/ministry automatically."
            />
          ) : (
            <>
              <TextField
                select
                label="Who is this event for?"
                value={form.scope}
                onChange={(e) => setForm({ ...form, scope: e.target.value })}
                fullWidth
                margin="normal"
              >
                <MenuItem value="general">General (whole church)</MenuItem>
                <MenuItem value="department">A specific department</MenuItem>
                <MenuItem value="ministry">A specific ministry</MenuItem>
              </TextField>
              {form.scope === "department" && (
                <TextField
                  select
                  label="Department"
                  value={form.scope_department_id}
                  onChange={(e) => setForm({ ...form, scope_department_id: e.target.value })}
                  fullWidth
                  margin="normal"
                >
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {form.scope === "ministry" && (
                <TextField
                  select
                  label="Ministry"
                  value={form.scope_ministry_id}
                  onChange={(e) => setForm({ ...form, scope_ministry_id: e.target.value })}
                  fullWidth
                  margin="normal"
                >
                  {ministries.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </>
          )}
        </FormSection>

        <FormSection>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Notify members (optional)
          </Typography>
          <TextField
            select
            label="Audience"
            value={form.audience}
            onChange={(e) => setForm({ ...form, audience: e.target.value })}
            fullWidth
            margin="normal"
          >
            <MenuItem value="all">All members</MenuItem>
            <MenuItem value="department">A department</MenuItem>
            <MenuItem value="ministry">A ministry</MenuItem>
          </TextField>

          {form.audience === "department" && (
            <TextField
              select
              label="Department"
              value={form.department_id}
              onChange={(e) => setForm({ ...form, department_id: e.target.value })}
              fullWidth
              margin="normal"
            >
              {departments.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {form.audience === "ministry" && (
            <TextField
              select
              label="Ministry"
              value={form.ministry_id}
              onChange={(e) => setForm({ ...form, ministry_id: e.target.value })}
              fullWidth
              margin="normal"
            >
              {ministries.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.channels.includes("sms")}
                  onChange={() => toggleChannel("sms")}
                />
              }
              label="SMS"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.channels.includes("email")}
                  onChange={() => toggleChannel("email")}
                />
              }
              label="Email"
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Notifications send once, immediately, when the event is created.
          </Typography>
        </FormSection>
      </FormDialog>

      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedEvent?.title}"?`}
        confirmLabel="Delete"
      />
    </Box>
  );
}

export default Events;
